import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../../../../components/Learner/Layout';
import QuickPracticeComponent from '../../../../components/Learner/QuickPractice';
import { PracticeErrorState, PracticeNoQuestionsState } from '../../../../components/Learner/Practice/PracticePageStates';
import { getQuickPracticeQuestions, submitPractice } from '../../../../services/practiceApi';
import { getLastOpenedUrl } from '../../../../utils/lastOpened';
import { usePracticeSession, isPageReload } from '../../../../hooks/usePracticeSession';
import PracticeLoadingOverlay from '../../../../components/Learner/Practice/PracticeLoadingOverlay';

const QuickPractice = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { saveSession, loadSession, clearSession } = usePracticeSession('quick-practice');

  const clearQuickPracticeStorage = useCallback(() => {
    clearSession();
    try { localStorage.removeItem(`qp-answers-${sessionData?.['session-id']}`); } catch (_) {}
  }, [clearSession, sessionData]);

  // Get practice parameters from location state or URL params
  const getPracticeParams = useCallback(() => {
    if (location.state) {
      return {
        courseId: location.state.courseId,
        subcourseId: location.state.subcourseId,
        skillId: location.state.skillId,
        lessonId: location.state.lessonId,
      };
    }
    const searchParams = new URLSearchParams(location.search);
    return {
      courseId: searchParams.get('course-id'),
      subcourseId: searchParams.get('subcourse-id'),
      skillId: searchParams.get('skill-id'),
      lessonId: searchParams.get('lesson-id'),
    };
  }, [location]);

  // Fetch questions from backend, or restore from localStorage on refresh
  useEffect(() => {
    const fetchOrRestore = async () => {
      setLoading(true);
      setError(null);

      const params = getPracticeParams();
      const hasParams = !!(params.courseId || params.subcourseId || params.skillId || params.lessonId);

      // Restore from localStorage on page reload, or when there are no API params
      if (isPageReload() || !hasParams) {
        const saved = loadSession();
        if (saved?.sessionData && saved?.questions?.length > 0) {
          setSessionData(saved.sessionData);
          setQuestions(saved.questions);
          setLoading(false);
          return;
        }
        if (!hasParams) {
          setError('Missing required parameters. Please navigate here from a practice selection page.');
          setLoading(false);
          return;
        }
      }

      const result = await getQuickPracticeQuestions(params);

      if (result.success && result.data) {
        setSessionData(result.data);
        const problems = result.data.problems || [];
        setQuestions(problems);
        saveSession(result.data, problems, location.state);
      } else {
        setError(result.message || 'Failed to load practice questions');
      }

      setLoading(false);
    };

    fetchOrRestore();
  }, [location, getPracticeParams]);

  const handlePracticeComplete = async (answers) => {
    if (!sessionData) {
      alert('Session data is missing. Please try again.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Map answers to API format
      const userAnswers = questions.map((question, questionIndex) => {
        const questionId = question['soal-id'] || question.id;
        
        // Find matching answer - try multiple matching strategies
        let existingAnswer = answers.find(a => {
          const answerQuestionId = a.questionId || a['soal-id'] || a['question-id'];
          return answerQuestionId === questionId || 
                 String(answerQuestionId) === String(questionId);
        });
        
        // Fallback: if no match found, try by index (answers should be in same order as questions)
        if (!existingAnswer && answers[questionIndex]) {
          existingAnswer = answers[questionIndex];
        }
        
        // Determine answer type based on question type
        const questionType = question.type || '';
        let answerType = 'text';
        if (questionType.includes('pilgan-single') || questionType.includes('sc')) {
          answerType = 'pilgan-single';
        } else if (questionType.includes('pilgan-multi') || questionType.includes('pilgan-multiple') || questionType.includes('mc')) {
          answerType = 'pilgan-multi';
        } else if (questionType.includes('fib') || questionType.includes('fill-blank') || questionType.includes('fill-in-the-blank')) {
          answerType = 'fib';
        } else if (questionType.includes('sequencing')) {
          answerType = 'sequencing';
        } else if (questionType.includes('pairing')) {
          answerType = 'pairing';
        } else if (questionType.includes('passage')) {
          answerType = 'passage';
        }
        
        // Format user answer based on type according to API documentation
        let userAnswer = existingAnswer?.answer;
        
        // Handle different answer formats according to API spec
        if (answerType === 'pilgan-single' || answerType === 'passage') {
          // For single choice: integer (index of selected option)
          // Note: 0 is a valid answer (first option), so check for undefined/null/empty string specifically
          if (userAnswer === undefined || userAnswer === null || userAnswer === '') {
            userAnswer = null; // API accepts null for unanswered
          } else {
            // Ensure it's an integer (0 is valid!)
            const numAnswer = typeof userAnswer === 'number' ? userAnswer : parseInt(userAnswer, 10);
            if (isNaN(numAnswer)) {
              userAnswer = null;
            } else {
              userAnswer = numAnswer;
            }
          }
        } else if (answerType === 'pilgan-multi') {
          // For multiple choice: array of integers (indices)
          if (!Array.isArray(userAnswer)) {
            userAnswer = userAnswer !== undefined && userAnswer !== null ? [userAnswer] : [];
          }
          // Ensure all are integers
          userAnswer = userAnswer.map(val => {
            const num = typeof val === 'number' ? val : parseInt(val, 10);
            return isNaN(num) ? null : num;
          }).filter(val => val !== null);
        } else if (answerType === 'fib') {
          // For FIB: array of integers (indices for each blank)
          if (typeof userAnswer === 'object' && userAnswer !== null && !Array.isArray(userAnswer)) {
            // Convert object {blankId: option} to array of option indices
            userAnswer = Object.values(userAnswer).map(val => {
              // If value is an object with id property, use that
              const optionId = typeof val === 'object' && val !== null ? (val.id || val.idx) : val;
              const num = typeof optionId === 'number' ? optionId : parseInt(optionId, 10);
              return isNaN(num) ? null : num;
            }).filter(val => val !== null);
          } else if (!Array.isArray(userAnswer)) {
            userAnswer = [];
          } else {
            // Ensure all are integers
            userAnswer = userAnswer.map(val => {
              const num = typeof val === 'number' ? val : parseInt(val, 10);
              return isNaN(num) ? null : num;
            }).filter(val => val !== null);
          }
        } else if (answerType === 'sequencing') {
          // For sequencing: array of integers (order)
          if (!Array.isArray(userAnswer)) {
            userAnswer = [];
          } else {
            // Convert to array of integers (indices or IDs)
            userAnswer = userAnswer.map((val, idx) => {
              // If it's an object with text, try to find index
              if (typeof val === 'object' && val !== null) {
                return idx; // Use position as index
              }
              const num = typeof val === 'number' ? val : parseInt(val, 10);
              return isNaN(num) ? idx : num;
            });
          }
        } else if (answerType === 'pairing') {
          // For pairing: object with left and right arrays
          if (Array.isArray(userAnswer)) {
            // Convert array of pairs to object format
            const left = [];
            const right = [];
            userAnswer.forEach(pair => {
              if (pair && typeof pair === 'object') {
                if (pair.left !== undefined) left.push(pair.left);
                if (pair.right !== undefined) right.push(pair.right);
              }
            });
            userAnswer = { left, right };
          } else if (typeof userAnswer !== 'object' || userAnswer === null) {
            userAnswer = { left: [], right: [] };
          }
        } else {
          // Default: try to preserve as-is
          if (userAnswer === undefined || userAnswer === null) {
            userAnswer = null;
          }
        }
        
        // Duration: default to 0 if not tracked (in milliseconds)
        const duration = existingAnswer?.duration || 0;
        
        return {
          'proset-id': question['proset-id'] || question.prosetId,
          'soal-id': questionId,
          type: answerType,
          'user-answer': userAnswer,
          duration: duration, // Duration in milliseconds
        };
      });

      // changed by Cursor: prioritas skill-id > lesson-id supaya skill practice kirim skill-id
      const params = getPracticeParams();
      const skillId = params.skillId || sessionData['skill-id'];
      const lessonId = skillId ? null : (params.lessonId || sessionData['lesson-id']);
      const subcourseId = (skillId || lessonId) ? null : (params.subcourseId || sessionData['subcourse-id']);
      const courseId = (skillId || lessonId || subcourseId) ? null : (params.courseId || sessionData['course-id']);

      if (!courseId && !subcourseId && !skillId && !lessonId) {
        setIsSubmitting(false);
        alert('Missing required information. Please try again from the practice selection page.');
        return;
      }

      const submissionData = {
        sessionId: sessionData['session-id'],
        practiceType: 'quick-practice',
        courseId,
        subcourseId,
        skillId,
        lessonId,
        userAnswers,
      };

      const result = await submitPractice(submissionData);

      setIsSubmitting(false);

      if (result.success && result.data) {
        const apiData = result.data;
        const params = getPracticeParams();
        const skillId = params.skillId || sessionData['skill-id'];
        const fromPage = location.state?.fromPage || '/learner/language-learning';
        
        // Determine language based on sessionData or fromPage
        let language = sessionData?.['course-name'] || null;
        if (!language) {
          if (fromPage === '/learner/english-language-learning' || fromPage?.includes('english')) {
            language = 'English language learning';
          } else if (fromPage === '/learner/japanese-language-learning' || fromPage?.includes('japanese')) {
            language = 'Japanese language learning';
          } else {
            language = 'German language learning';
          }
        }
        
        const results = {
          language,
          courseType: skillId ? 'Skill Practice' : 'Course Practice',
          level: sessionData?.['subcourse-name'] || 'All Levels',
          score: apiData.score || 0,
          correctCount: apiData['correct-count'] || apiData.correctCount || 0,
          totalQuestions: apiData['total-questions'] || apiData.totalQuestions || questions.length,
          currentLevel: apiData['current-level'] || apiData.currentLevel || 2,
          previousLevel: apiData['previous-level'] || apiData.previousLevel || 1.8,
          deltaLevel: apiData['delta-level'] || apiData.deltaLevel || 0.2,
          practiceParams: params,
          apiResponse: apiData,
          originalQuestions: questions,
          userAnswers: answers,
          sessionData: sessionData,
          fromPage,
        };
        
        clearQuickPracticeStorage();
        navigate(skillId ? '/learner/skill-result' : '/learner/result', { state: results });
      } else {
        const correctCount = answers.filter(a => a.isCorrect).length;
        const totalQuestions = questions.length;
        const score = correctCount;
        const deltaLevel = (correctCount * 10) / 100;
        const previousLevel = 1.8;
        const currentLevel = previousLevel + deltaLevel;
        const params = getPracticeParams();
        const skillId = params.skillId || sessionData['skill-id'];
        const fromPage = location.state?.fromPage || '/learner/language-learning';
        
        // Determine language based on sessionData or fromPage
        let language = sessionData?.['course-name'] || null;
        if (!language) {
          if (fromPage === '/learner/english-language-learning' || fromPage?.includes('english')) {
            language = 'English language learning';
          } else if (fromPage === '/learner/japanese-language-learning' || fromPage?.includes('japanese')) {
            language = 'Japanese language learning';
          } else {
            language = 'German language learning';
          }
        }
        
        const results = {
          language,
          courseType: skillId ? 'Skill Practice' : 'Course Practice',
          level: sessionData?.['subcourse-name'] || 'All Levels',
          score,
          correctCount,
          totalQuestions,
          currentLevel,
          previousLevel,
          deltaLevel,
          practiceParams: params,
          apiResponse: null,
          originalQuestions: questions,
          userAnswers: answers,
          sessionData: sessionData,
          fromPage,
        };
        
        clearQuickPracticeStorage();
        navigate(skillId ? '/learner/skill-result' : '/learner/result', { state: results });

        if (result.message) {
          console.error('Submit practice error:', result.message);
        }
      }
    } catch (error) {
      console.error('Error submitting practice:', error);
      setIsSubmitting(false);
      
      const correctCount = answers.filter(a => a.isCorrect).length;
      const totalQuestions = questions.length;
      const score = correctCount;
      const deltaLevel = (correctCount * 10) / 100;
      const previousLevel = 1.8;
      const currentLevel = previousLevel + deltaLevel;
      const params = getPracticeParams();
      const skillId = params.skillId || sessionData?.['skill-id'];
      const fromPage = location.state?.fromPage || '/learner/language-learning';
      
      // Determine language based on sessionData or fromPage
      let language = sessionData?.['course-name'] || null;
      if (!language) {
        language = (fromPage === '/learner/english-language-learning' || fromPage?.includes('english')) 
          ? 'English language learning' 
          : 'German language learning';
      }
      
      const results = {
        language,
        courseType: skillId ? 'Skill Practice' : 'Course Practice',
        level: sessionData?.['subcourse-name'] || 'All Levels',
        score,
        correctCount,
        totalQuestions,
        currentLevel,
        previousLevel,
        deltaLevel,
        practiceParams: params,
        apiResponse: null,
        originalQuestions: questions,
        userAnswers: answers,
        sessionData: sessionData,
        fromPage,
      };
      
      clearQuickPracticeStorage();
      navigate(skillId ? '/learner/skill-result' : '/learner/result', { state: results });
    }
  };

  const handleClose = () => {
    clearQuickPracticeStorage();

    // Priority 1: Use fromPage from location state (from English Language Learning Page)
    const fromPage = location.state?.fromPage;
    if (fromPage) {
      console.log('📖 QuickPractice: Navigating back to fromPage:', fromPage);
      navigate(fromPage);
      return;
    }
    
    // Priority 2: Navigate back to last opened URL
    const lastOpenedUrl = getLastOpenedUrl();
    if (lastOpenedUrl) {
      console.log('📖 QuickPractice: Navigating back to lastOpenedUrl:', lastOpenedUrl);
      navigate(lastOpenedUrl);
      return;
    }
    
    // Priority 3: Fallback to default
    console.log('📖 QuickPractice: No fromPage or lastOpenedUrl found, navigating to default location');
    navigate('/learner/language-learning');
  };

  // Loading state
  if (loading) {
    return <PracticeLoadingOverlay visible text="Loading practice questions..." />;
  }

  // Error state
  if (error) {
    return <PracticeErrorState title="Quick Practice" error={error} onBack={handleClose} />;
  }

  // No questions state
  if (!questions || questions.length === 0) {
    return <PracticeNoQuestionsState title="Quick Practice" onBack={handleClose} />;
  }

  return (
    <Layout hideSidebar>
      <div className="w-full p-6 md:p-4 md:px-6 flex flex-col items-center justify-center min-h-screen max-md:p-0 max-md:min-h-[100dvh] max-md:items-stretch max-md:justify-start">
        <PracticeLoadingOverlay
          visible={isSubmitting}
          text="Submitting your answers..."
          subtext="Please wait while we grade your practice"
        />
          <>
            <QuickPracticeComponent 
              practiceTitle="Quick Practice"
              questions={questions}
              onComplete={handlePracticeComplete}
              onClose={handleClose}
              hideCompletedScreen={true}
              sessionData={sessionData}
            />
          </>
      </div>
    </Layout>
  );
};

export default QuickPractice;